/* ============================================================
   Portl Track Order controller — window.PortlTrackOrder
   Vanilla JS, no deps. One instance per [data-pto] section.

   API layer is fully isolated + admin-configurable (no hardcoded URLs):
     url = {Base URL}{Track endpoint}/{AWB}
   Base URL + endpoint come from the section settings (data-* attributes).
   Demo mode ON renders the embedded sample; OFF calls the live API.

   Response is normalised into the render shape the UI already consumes, so
   the design/layout is unchanged. All dynamic text is HTML-escaped (XSS-safe),
   values are read with optional-chaining-style guards, and JSON is parsed
   defensively.
   ============================================================ */
window.PortlTrackOrder = (function () {
  'use strict';

  var MILESTONES = ['confirmed', 'packed', 'shipped', 'out_for_delivery', 'delivered'];
  var MLABEL = { confirmed: 'Confirmed', packed: 'Packed', shipped: 'Shipped', out_for_delivery: 'Out for Delivery', delivered: 'Delivered' };
  var EXC = { cancelled: 'Cancelled', returned: 'Returned', failed: 'Failed Delivery' };
  // API status enum -> internal milestone / exception key
  var STATUS_MAP = {
    LABEL_GENERATED: 'confirmed', CONFIRMED: 'confirmed', PACKED: 'packed',
    IN_TRANSIT: 'shipped', SHIPPED: 'shipped', OUT_FOR_DELIVERY: 'out_for_delivery',
    DELIVERED: 'delivered', CANCELLED: 'cancelled', RETURNED: 'returned', FAILED: 'failed'
  };
  var COURIERS = {
    bluedart: { label: 'Blue Dart', code: 'BD' }, delhivery: { label: 'Delhivery', code: 'DL' },
    dtdc: { label: 'DTDC', code: 'DT' }, xpressbees: { label: 'XpressBees', code: 'XB' },
    ecomexpress: { label: 'Ecom Express', code: 'EE' }, shiprocket: { label: 'Shiprocket', code: 'SR' },
    truxcargo: { label: 'Truxcargo', code: 'TX' }
  };
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  /* ---------- utilities ---------- */
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function titleCase(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\b([a-z])/g, function (_, c) { return c.toUpperCase(); });
  }
  function courierInfo(code) {
    var k = String(code || '').toLowerCase().replace(/[^a-z]/g, '');
    if (COURIERS[k]) return COURIERS[k];
    var label = code ? titleCase(code) : 'Courier';
    return { label: label, code: (label.replace(/[^A-Za-z]/g, '').slice(0, 2) || 'CO').toUpperCase() };
  }
  // ISO -> { date:"31 Jul 2026", time:"4:59 PM", full:"31 Jul 2026 · 4:59 PM" } in the visitor's local timezone
  function formatDate(iso) {
    if (!iso) return { date: '', time: '', full: '' };
    var dt = new Date(iso);
    if (isNaN(dt.getTime())) return { date: String(iso), time: '', full: String(iso) };
    var date = pad(dt.getDate()) + ' ' + MONTHS[dt.getMonth()] + ' ' + dt.getFullYear();
    var h = dt.getHours(), m = dt.getMinutes(), ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    var time = h + ':' + pad(m) + ' ' + ampm;
    return { date: date, time: time, full: date + ' · ' + time };
  }
  // Long form used only by the Delivery card: "27 July 2026 • 10:39 PM" (local TZ).
  function formatDateLong(iso) {
    if (!iso) return null;
    var dt = new Date(iso);
    if (isNaN(dt.getTime())) return null;
    var date = dt.getDate() + ' ' + MONTHS_LONG[dt.getMonth()] + ' ' + dt.getFullYear();
    var h = dt.getHours(), m = dt.getMinutes(), ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    var time = h + ':' + pad(m) + ' ' + ampm;
    return { date: date, time: time, full: date + ' • ' + time };
  }
  // Parse an estimated-delivery string ("01 August 2026", ISO, …) into a real
  // local Date. Prefers an explicit "DD Month YYYY" match to avoid UTC/local
  // day-shift ambiguity; falls back to the native parser.
  function parseEstimate(str) {
    if (!str) return null;
    var m = String(str).match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) {
      var name = m[2].toLowerCase(), mi = -1;
      for (var i = 0; i < 12; i++) { if (MONTHS_LONG[i].toLowerCase() === name || MONTHS[i].toLowerCase() === name.slice(0, 3)) { mi = i; break; } }
      if (mi >= 0) return new Date(+m[3], mi, +m[1]);
    }
    var dt = new Date(str);
    return isNaN(dt.getTime()) ? null : dt;
  }
  // True only when the estimated day is strictly before today (local, date-only).
  // "today or in the future" stays ESTIMATED.
  function isPastEstimate(str) {
    var dt = parseEstimate(str);
    if (!dt) return false;
    var est = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    var today = new Date(); today.setHours(0, 0, 0, 0);
    return est.getTime() < today.getTime();
  }
  function mapStatus(raw) { return STATUS_MAP[String(raw || '').toUpperCase()] || null; }
  function formatStatus(raw, statusRaw) {
    var k = mapStatus(raw);
    if (k && MLABEL[k]) return MLABEL[k];
    if (k && EXC[k]) return EXC[k];
    return titleCase(statusRaw || raw || 'Status');
  }
  function buildApiUrl(base, endpoint, awb) {
    var b = String(base || '').replace(/\/+$/, '');
    var e = String(endpoint || '');
    if (e && e.charAt(0) !== '/') e = '/' + e;
    e = e.replace(/\/+$/, '');
    return b + e + '/' + encodeURIComponent(awb);
  }

  /* ---------- normalise API response -> render shape ---------- */
  function deriveFromTimeline(tl) {
    var best = null, bestIdx = -1;
    (tl || []).forEach(function (t) {
      var k = mapStatus(t && t.status), i = MILESTONES.indexOf(k);
      if (i > bestIdx) { bestIdx = i; best = k; }
    });
    return best;
  }
  function normalize(json, awb) {
    var d = (json && json.data) ? json.data : {};
    var ci = courierInfo(d.courier);
    var statusKey = mapStatus(d.status) || deriveFromTimeline(d.timeline) || 'confirmed';

    var mt = {};
    (d.timeline || []).forEach(function (t) {
      var k = mapStatus(t && t.status);
      if (k && MILESTONES.indexOf(k) >= 0 && !mt[k]) mt[k] = formatDate(t.timestamp).full;
    });

    var tl = (d.timeline || []).slice().reverse().map(function (t, i) {
      return {
        status: titleCase((t && (t.statusRaw || t.status)) || ''),
        location: titleCase((t && t.location) || ''),
        time: formatDate(t && t.timestamp).full,
        state: i === 0 ? 'current' : 'done'
      };
    });

    var loc = titleCase(d.currentLocation || '');
    var awbNum = d.trackingNumber || awb || '';

    // Delivered timestamp: prefer the DELIVERED timeline event, else lastUpdated
    // when the overall status is delivered. Raw ISO — parsed/compared as a Date.
    var deliveredAt = '';
    (d.timeline || []).forEach(function (t) { if (mapStatus(t && t.status) === 'delivered' && t && t.timestamp) deliveredAt = t.timestamp; });
    if (!deliveredAt && statusKey === 'delivered') deliveredAt = d.lastUpdated || '';

    return {
      found: true,
      order_number: d.orderNumber || awbNum,
      product_name: d.orderNumber ? ('Order ' + d.orderNumber) : ('AWB ' + awbNum),
      summary_sub: ci.label + (loc ? ' · ' + loc : ''),
      status: statusKey,
      status_label: formatStatus(d.status, d.statusRaw),
      milestone_times: mt,
      courier: { name: ci.label, code: ci.code, awb: awbNum, hub: loc, tracking_url: '' },
      delivery: { estimate: d.estimatedDelivery || '', delivered_at: deliveredAt, last_updated: d.lastUpdated || '' },
      timeline: tl
    };
  }

  /* ---------- session cache ---------- */
  function cacheKey(awb) { return 'pto_track_' + awb; }
  function getCachedResponse(awb) { try { var r = window.sessionStorage.getItem(cacheKey(awb)); return r ? JSON.parse(r) : null; } catch (e) { return null; } }
  function cacheResponse(awb, d) { try { window.sessionStorage.setItem(cacheKey(awb), JSON.stringify(d)); } catch (e) {} }

  function create(root) {
    var cfg = {
      demo: root.getAttribute('data-demo') === '1',
      baseUrl: root.getAttribute('data-api-base') || '',
      endpoint: root.getAttribute('data-api-endpoint') || '/api/v1/track',
      minLen: parseInt(root.getAttribute('data-min-len'), 10) || 1,
      maxLen: parseInt(root.getAttribute('data-max-len'), 10) || 80,
      msgLoading: root.getAttribute('data-msg-loading') || '',
      msgUnavailable: root.getAttribute('data-msg-unavailable') || '',
      supportEmail: root.getAttribute('data-support-email') || '',
      supportPhone: root.getAttribute('data-support-phone') || '',
      msgNetwork: root.getAttribute('data-msg-network') || '',
      msgEmpty: root.getAttribute('data-msg-empty') || '',
      resultLabel: root.getAttribute('data-result-label') || '%order%',
      errRequired: root.getAttribute('data-err-required') || 'Required.',
      errOrder: root.getAttribute('data-err-order') || 'Invalid.'
    };

    var el = {
      form: root.querySelector('[data-pto-form]'),
      order: root.querySelector('[data-pto-order]'),
      submit: root.querySelector('[data-pto-submit]'),
      result: root.querySelector('[data-pto-result]'),
      resultLabel: root.querySelector('[data-pto-result-label]'),
      stLoading: root.querySelector('[data-pto-state="loading"]'),
      stLoadingMsg: root.querySelector('[data-pto-loading-msg]'),
      stMessage: root.querySelector('[data-pto-state="message"]'),
      stMessageMsg: root.querySelector('[data-pto-state-msg]'),
      found: root.querySelector('[data-pto-state="found"]'),
      summary: root.querySelector('[data-pto-summary]'),
      progress: root.querySelector('[data-pto-progress]'),
      timeline: root.querySelector('[data-pto-timeline]'),
      delivery: root.querySelector('[data-pto-delivery]')
    };
    if (!el.form || !el.result || !el.order) return null;

    var submitHTML = el.submit ? el.submit.innerHTML : '';
    var busy = false;

    var demoData = null;
    var demoNode = root.querySelector('[data-pto-demo]');
    if (demoNode) { try { demoData = JSON.parse(demoNode.textContent); } catch (e) { demoData = null; } }

    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
        }, { threshold: 0.15 })
      : null;

    /* ---------- validation ---------- */
    function fieldErr(name, msg) {
      var inp = el[name];
      var e = root.querySelector('[data-err-for="' + name + '"]');
      if (inp) inp.classList.toggle('is-invalid', !!msg);
      if (e) { e.textContent = msg || ''; e.hidden = !msg; }
    }
    function validOrder() {
      var v = (el.order.value || '').trim();
      if (!v) return { ok: false, msg: cfg.errRequired };
      if (v.length < cfg.minLen || v.length > cfg.maxLen) return { ok: false, msg: cfg.errOrder };
      return { ok: true };
    }
    function validate(showErrors) {
      var o = validOrder();
      if (showErrors) fieldErr('order', o.ok ? '' : o.msg);
      if (el.submit) el.submit.disabled = !o.ok || busy;
      return o.ok;
    }

    /* ---------- states ---------- */
    function hideStates() { [el.stLoading, el.stMessage, el.found].forEach(function (n) { if (n) n.hidden = true; }); }
    function openResult() { el.result.hidden = false; }
    function clearResults() { el.result.hidden = true; hideStates(); }
    function showLoading() {
      openResult(); hideStates();
      if (el.stLoadingMsg) el.stLoadingMsg.textContent = cfg.msgLoading;
      if (el.stLoading) el.stLoading.hidden = false;
      if (el.submit) { el.submit.disabled = true; el.submit.setAttribute('aria-busy', 'true'); el.submit.textContent = 'Tracking…'; }
    }
    function hideLoading() {
      if (el.submit) { el.submit.removeAttribute('aria-busy'); el.submit.innerHTML = submitHTML; }
      validate(false);
    }
    function showMessage(msg) {
      openResult(); hideStates();
      if (el.stMessageMsg) el.stMessageMsg.textContent = msg || '';
      if (el.stMessage) el.stMessage.hidden = false;
    }
    function showEmpty() { showMessage(cfg.msgEmpty); }
    function showNetwork() { showMessage(cfg.msgNetwork); }
    // Invalid AWB / un-integrated courier → reassuring, admin-editable message
    // with clickable support email + phone (all escaped; safe HTML links).
    function showUnavailable() {
      openResult(); hideStates();
      var html = esc(cfg.msgUnavailable || '');
      html = html.replace('%email%', cfg.supportEmail ? ('<a href="mailto:' + esc(cfg.supportEmail) + '">' + esc(cfg.supportEmail) + '</a>') : '');
      var tel = (cfg.supportPhone || '').replace(/[^0-9+]/g, '');
      html = html.replace('%phone%', cfg.supportPhone ? ('<a href="tel:' + esc(tel) + '">' + esc(cfg.supportPhone) + '</a>') : '');
      if (el.stMessageMsg) el.stMessageMsg.innerHTML = html;
      if (el.stMessage) el.stMessage.hidden = false;
    }
    function showError() { showUnavailable(); }
    function showNotFound() { showUnavailable(); }
    function showFound(d) {
      openResult(); hideStates();
      if (el.resultLabel) el.resultLabel.textContent = cfg.resultLabel.replace('%order%', d.order_number || '');
      if (el.found) el.found.hidden = false;
    }

    /* ---------- renderers (UI unchanged) ---------- */
    function progressState(d) {
      var exception = EXC.hasOwnProperty(d.status);
      var idx;
      if (exception) { idx = 0; MILESTONES.forEach(function (k, i) { if (d.milestone_times && d.milestone_times[k]) idx = i; }); }
      else { idx = MILESTONES.indexOf(d.status); if (idx < 0) idx = 0; }
      return { idx: idx, pct: MILESTONES.length > 1 ? (idx / (MILESTONES.length - 1)) * 100 : 0, exception: exception };
    }
    function renderSummary(d) {
      if (!el.summary) return;
      var thumb = d.product_image
        ? '<img src="' + esc(d.product_image) + '" alt="" loading="lazy" decoding="async">'
        : '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h10" stroke="var(--pto-accent,#00FFBA)" stroke-width="2" stroke-linecap="round"/></svg>';
      var exception = EXC.hasOwnProperty(d.status);
      var sub = d.summary_sub || ('Order ' + (d.order_number || '') + (d.placed_date ? ' · Placed ' + d.placed_date : '') + (d.quantity ? ' · Qty ' + d.quantity : ''));
      el.summary.innerHTML =
        '<div class="pto-summary__thumb">' + thumb + '</div>' +
        '<div class="pto-summary__meta"><div class="oname">' + esc(d.product_name || 'Your order') + '</div>' +
        '<div class="osub">' + esc(sub) + '</div></div>' +
        '<div class="pto-status-badge' + (exception ? ' pto-status-badge--alert' : '') + '"><span class="dot"></span>' + esc(d.status_label || '') + '</div>';
    }
    function courierHTML(d) {
      var c = d.courier || {};
      if (!c.name && !c.awb) return '';
      var logo = c.logo_image ? '<img src="' + esc(c.logo_image) + '" alt="">' : esc(c.code || (c.name || '').slice(0, 2).toUpperCase());
      var awbBlock = c.tracking_url
        ? '<a class="l2" href="' + esc(c.tracking_url) + '" target="_blank" rel="noopener">AWB ' + esc(c.awb || '') + '</a>'
        : '<div class="l2">AWB ' + esc(c.awb || '') + '</div>';
      return '<div class="pto-courier"><div class="cs-logo">' + logo + '</div>' +
        '<div class="cs-meta"><div class="l1">Shipped via ' + esc(c.name || 'courier') + '</div>' + awbBlock + '</div>' +
        (c.awb ? '<button type="button" class="pto-btn-copy" data-pto-copy="' + esc(c.awb) + '"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M5 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" stroke-width="1.8"/></svg>Copy</button>' : '') +
        (c.hub ? '<div class="cs-location"><div class="l1">Currently at</div><div class="l2">' + esc(c.hub) + '</div></div>' : '') + '</div>';
    }
    function renderCourier(d) {
      if (!el.progress) return;
      var holder = el.progress.querySelector('[data-pto-courier-holder]');
      if (holder) holder.innerHTML = courierHTML(d);
    }
    function renderProgress(d) {
      if (!el.progress) return;
      var ps = progressState(d);
      var nodes = MILESTONES.map(function (k, i) {
        var cls = i < ps.idx ? 'done' : (i === ps.idx ? 'current' : '');
        var time = (d.milestone_times && d.milestone_times[k]) ? d.milestone_times[k] : '';
        return '<div class="pto-p-node ' + cls + '"><div class="n-label">' + esc(MLABEL[k]) + '</div><div class="n-time">' + esc(time) + '</div></div>';
      }).join('');
      el.progress.className = 'pto-progress-card' + (ps.exception ? ' pto-progress--exception' : '');
      el.progress.innerHTML =
        '<div class="pto-progress-track-wrap"><div class="pto-progress-track"><div class="pto-progress-fill" data-fill></div></div><div class="pto-progress-dot" data-dot></div></div>' +
        '<div class="pto-progress-nodes">' + nodes + '</div><div data-pto-courier-holder></div>';
      renderCourier(d);
      var fill = el.progress.querySelector('[data-fill]'), dot = el.progress.querySelector('[data-dot]');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (fill) fill.style.width = ps.pct + '%';
          if (dot) dot.style.left = ps.pct + '%';
        });
      });
    }
    function renderTimeline(d) {
      if (!el.timeline) return;
      el.timeline.innerHTML = (d.timeline || []).map(function (t, i) {
        var st = t.state === 'current' ? 'current' : (t.state === 'pending' ? '' : 'done');
        return '<div class="pto-t-item ' + st + '" style="transition-delay:' + (i * 60) + 'ms">' +
          '<div class="t-status">' + esc(t.status || '') + '</div>' +
          (t.location ? '<div class="t-loc">' + esc(t.location) + '</div>' : '') +
          (t.time ? '<div class="t-time">' + esc(t.time) + '</div>' : '') + '</div>';
      }).join('');
      var nodes = el.timeline.querySelectorAll('.pto-t-item');
      if (io) nodes.forEach(function (n) { io.observe(n); });
      else nodes.forEach(function (n) { n.classList.add('is-in'); });
    }
    // Delivery card. Title + text follow the priority:
    //   1. status Delivered           -> "Delivered"          (delivered date + "Delivered on …")
    //   2. now past estimated date    -> "Delivery expected"  (est date + "Last updated …")
    //   3. today / future estimate    -> "Estimated delivery" (est date + "Last updated …")
    // The .dl label is uppercased by CSS, so Title Case here is fine.
    function renderDelivery(d) {
      if (!el.delivery) return;
      var dv = d.delivery || {};
      var delivered = d.status === 'delivered';
      var title, value, sub = '';

      if (delivered) {
        title = 'Delivered';
        var dat = dv.delivered_at ? formatDateLong(dv.delivered_at) : null;
        if (dat) { value = dat.date; sub = 'Delivered on ' + dat.full; }
        else { value = dv.estimate || '—'; }                 // fallback: estimate date for display
      } else {
        value = dv.estimate || '—';
        title = isPastEstimate(dv.estimate) ? 'Delivery expected' : 'Estimated delivery';
      }

      if (!sub) {
        if (dv.last_updated) { var lu = formatDateLong(dv.last_updated); if (lu) sub = 'Last updated ' + lu.full; }
        else if (dv.note) sub = dv.note;                     // demo-shape fallback
      }

      if ((!value || value === '—') && !sub) { el.delivery.hidden = true; return; }
      el.delivery.hidden = false;
      el.delivery.innerHTML =
        '<div class="dl">' + esc(title) + '</div><div class="dv">' + esc(value) + '</div>' +
        (sub ? '<div class="dsub">' + esc(sub) + '</div>' : '');
    }
    function renderOrder(d) {
      if (!d || d.found === false) { showNotFound(); return; }
      renderSummary(d); renderProgress(d); renderTimeline(d); renderDelivery(d); showFound(d);
    }

    /* ---------- history ---------- */
    function afterSuccess(awb) {
      try { var u = new URL(window.location.href); u.searchParams.set('awb', awb); window.history.replaceState({}, '', u.toString()); } catch (e) {}
    }

    /* ---------- main flow ---------- */
    function trackShipment(awbInput) {
      var awb = (awbInput != null ? String(awbInput) : (el.order.value || '')).trim();
      if (!awb) { fieldErr('order', cfg.errRequired); return; }
      if (awb.length < cfg.minLen || awb.length > cfg.maxLen) { fieldErr('order', cfg.errOrder); return; }
      fieldErr('order', '');
      if (busy) return;   // prevent duplicate requests / rapid Enter

      // Session cache → skip the network for a repeat AWB.
      var cached = getCachedResponse(awb);
      if (cached) { renderOrder(cached); afterSuccess(awb); return; }

      busy = true; showLoading();
      fetch(buildApiUrl(cfg.baseUrl, cfg.endpoint, awb), { method: 'GET', headers: { 'Accept': 'application/json' }, mode: 'cors', credentials: 'omit' })
        .then(function (res) {
          if (res.status === 404) return { __notfound: true };
          if (!res.ok) return { __error: true };
          return res.json().catch(function () { return { __error: true }; });
        })
        .then(function (json) {
          busy = false; hideLoading();
          if (!json || json.__error) { showError(); return; }
          if (json.__notfound || json.success === false || !json.data) { showNotFound(); return; }
          var d = normalize(json, awb);
          cacheResponse(awb, d);
          renderOrder(d);
          afterSuccess(awb);
        })
        .catch(function () { busy = false; hideLoading(); showNetwork(); });
    }

    /* ---------- events ---------- */
    el.form.addEventListener('submit', function (e) { e.preventDefault(); trackShipment(); });
    el.order.addEventListener('input', function () {
      el.order.classList.remove('is-invalid');
      var e = root.querySelector('[data-err-for="order"]'); if (e) e.hidden = true;
      validate(false);
    });
    // Copy (delegated — courier strip is rendered dynamically)
    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-pto-copy]') : null;
      if (!btn) return;
      var val = btn.getAttribute('data-pto-copy');
      var done = function () { var o = btn.innerHTML; btn.textContent = 'Copied'; window.setTimeout(function () { btn.innerHTML = o; }, 1400); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(val).then(done, done);
      else { try { var t = document.createElement('textarea'); t.value = val; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); done(); } catch (er) {} }
    });

    /* ---------- init ---------- */
    validate(false);
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var urlAwb = null;
    try { urlAwb = new URL(window.location.href).searchParams.get('awb'); } catch (e) {}
    if (urlAwb) {
      // Deep-link: always fetch live from the API.
      el.order.value = urlAwb; validate(false); trackShipment(urlAwb);
    } else if (cfg.demo && demoData && designMode) {
      // Sample preview ONLY inside the theme editor, so merchants can style the
      // populated layout. The live storefront never shows sample data — it waits
      // for a real search, which always hits the API.
      if (!el.order.value) el.order.value = demoData.order_number || '';
      validate(false);
      renderOrder(demoData);
    }

    return {
      trackShipment: trackShipment,
      buildApiUrl: function (awb) { return buildApiUrl(cfg.baseUrl, cfg.endpoint, awb); },
      renderShipment: renderOrder, renderOrder: renderOrder, renderTimeline: renderTimeline,
      updateStepper: renderProgress, renderProgress: renderProgress, renderCourier: renderCourier,
      renderSummary: renderSummary, renderDelivery: renderDelivery,
      showLoading: showLoading, hideLoading: hideLoading, showError: showError, showEmpty: showEmpty,
      showNotFound: showNotFound, showUnavailable: showUnavailable, showNetwork: showNetwork, showSuccess: showFound, clearResults: clearResults,
      cacheResponse: cacheResponse, getCachedResponse: getCachedResponse, formatDate: formatDate, formatStatus: formatStatus
    };
  }

  var instances = [];
  function boot() {
    document.querySelectorAll('[data-pto]').forEach(function (root) {
      if (root.__ptoInit) return;
      root.__ptoInit = true;
      try { var inst = create(root); if (inst) instances.push(inst); } catch (e) { if (window.console) console.warn('[PortlTrackOrder]', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);

  return { boot: boot, instances: instances, buildApiUrl: buildApiUrl, formatDate: formatDate, formatStatus: formatStatus, normalize: normalize };
})();
